// packages/shared/src/utils/product.utils.ts
/**
 * Utility functions for handling multi-author and multi-category products
 */
/**
 * Split comma-separated authors into an array
 * @param authors - Comma-separated author string
 * @returns Array of author names
 */
export const splitAuthors = (authors) => {
    if (!authors)
        return [];
    return authors
        .split(',')
        .map((author) => author.trim())
        .filter(Boolean);
};
/**
 * Join array of authors into comma-separated string
 * @param authors - Array of author names
 * @returns Comma-separated author string
 */
export const joinAuthors = (authors) => {
    return authors.filter(Boolean).join(', ');
};
/**
 * Format authors for display (e.g., "Author 1, Author 2 & Author 3")
 * @param authors - Comma-separated author string or array
 * @param maxDisplay - Maximum number of authors to display before showing "et al."
 * @returns Formatted author string
 */
export const formatAuthorsDisplay = (authors, maxDisplay = 3) => {
    if (!authors)
        return 'Unknown Author';
    const authorArray = Array.isArray(authors) ? authors : splitAuthors(authors);
    if (authorArray.length === 0)
        return 'Unknown Author';
    if (authorArray.length === 1)
        return authorArray[0];
    if (authorArray.length === 2)
        return authorArray.join(' & ');
    if (authorArray.length > maxDisplay) {
        const displayed = authorArray.slice(0, maxDisplay).join(', ');
        return `${displayed} et al.`;
    }
    const lastAuthor = authorArray[authorArray.length - 1];
    const otherAuthors = authorArray.slice(0, -1).join(', ');
    return `${otherAuthors} & ${lastAuthor}`;
};
/**
 * Split comma-separated category IDs into an array
 * @param categoryIds - Comma-separated category ID string
 * @returns Array of category IDs
 */
export const splitCategories = (categoryIds) => {
    if (!categoryIds)
        return [];
    return categoryIds
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
};
/**
 * Join array of category IDs into comma-separated string
 * @param categoryIds - Array of category IDs
 * @returns Comma-separated category ID string
 */
export const joinCategories = (categoryIds) => {
    return categoryIds.filter(Boolean).join(',');
};
/**
 * Get the first/primary category ID from comma-separated string
 * @param categoryIds - Comma-separated category ID string
 * @returns First category ID or empty string
 */
export const getPrimaryCategory = (categoryIds) => {
    const categories = splitCategories(categoryIds);
    return categories[0] || '';
};
/**
 * Sync categories: ensures categoryId string and categories array are in sync
 * Use this when saving products to Firebase
 * @param categoryId - Comma-separated category ID string
 * @returns Object with both categoryId and categories array
 */
export const syncCategories = (categoryId) => {
    const categories = splitCategories(categoryId);
    return {
        categoryId,
        categories,
    };
};
